// Portal do Cliente + Portal do Motorista (mocked prototype)
import React from 'react';
import { LayoutDashboard, FileSignature, DollarSign, Folder, Wrench, MessageSquare, User, HelpCircle, CalendarDays, Truck, ClipboardCheck, Fuel, AlertOctagon, IdCard, MapPin } from 'lucide-react';
import { MockPageShell, MockKpi, MockToolbar, MockTable, StatusBadge, ChartCard, MockLine, MockBar } from '../../components/MockUI';
import { mockFinance, mockOS, mockTrips, mockFuel, mockRoutes, genSeries } from '../../lib/mockData';
import { Button } from '../../components/ui/button';

// ============ Portal do Cliente ============

const bcC = (l) => [{ label: 'Portal do Cliente', to: '/portal-cliente' }, { label: l }];

export function PortalClienteDashboard() {
  return <MockPageShell title="Olá, Prefeitura de Canoas 👋" subtitle="Acompanhe seus contratos, serviços e financeiro em um só lugar"
    breadcrumbs={[{ label: 'Portal do Cliente' }]}
    kpis={[
      <MockKpi key="1" icon={FileSignature} label="Contratos ativos" value="2" tone="success" />,
      <MockKpi key="2" icon={DollarSign} label="A vencer" value="R$ 128.000" tone="warn" />,
      <MockKpi key="3" icon={Wrench} label="OS em andamento" value="1" />,
      <MockKpi key="4" icon={MessageSquare} label="Mensagens não lidas" value="3" tone="info" />,
    ]}
    charts={[
      <ChartCard key="1" title="Faturamento contratado (6m)"><MockBar data={genSeries(120, 20)} color="#3B82F6" /></ChartCard>,
      <ChartCard key="2" title="Utilização por rota"><MockLine data={genSeries(85, 15)} color="#10B981" /></ChartCard>,
    ]}
  />;
}

export function PortalContratos() {
  const rows = [
    { code: 'CT-002', kind: 'Contrato público', start: '15/06/2024', end: '14/06/2027', value: 'R$ 1.480.000', status: 'Ativo' },
    { code: 'CT-002B', kind: 'Aditivo', start: '10/09/2025', end: '14/06/2027', value: 'R$ 240.000', status: 'Ativo' },
  ];
  return <MockPageShell title="Meus Contratos" breadcrumbs={bcC('Contratos')}
    table={<MockTable columns={[{ key: 'code', label: 'Contrato', mono: true }, { key: 'kind', label: 'Tipo' }, { key: 'start', label: 'Início' }, { key: 'end', label: 'Fim' }, { key: 'value', label: 'Valor', align: 'right' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Ativo: 'green' }} />}
  />;
}

export function PortalFinanceiro() {
  const rows = mockFinance.filter(f => f.kind === 'receber').map(f => ({ ...f, value: `R$ ${f.value.toLocaleString('pt-BR')}` }));
  return <MockPageShell title="Meu Financeiro" subtitle="Faturas, boletos e pagamentos"
    breadcrumbs={bcC('Financeiro')}
    kpis={[
      <MockKpi key="1" icon={DollarSign} label="Em aberto" value="R$ 173K" />,
      <MockKpi key="2" icon={DollarSign} label="Pago em 30d" value="R$ 245K" tone="success" />,
    ]}
    table={<MockTable columns={[{ key: 'doc', label: 'NF', mono: true }, { key: 'category', label: 'Referência' }, { key: 'due', label: 'Vencimento' }, { key: 'value', label: 'Valor', align: 'right' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Aberto: 'amber', Pago: 'green', Vencido: 'red' }} />}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Gerar 2ª via</Button>}
  />;
}

export function PortalDocumentos() {
  const rows = [
    { name: 'Contrato CT-002 assinado.pdf', kind: 'PDF', size: '2.4 MB', date: '15/06/2024' },
    { name: 'Aditivo CT-002B.pdf', kind: 'PDF', size: '1.1 MB', date: '10/09/2025' },
    { name: 'Certidão Regularidade Fiscal.pdf', kind: 'PDF', size: '380 KB', date: '02/02/2026' },
    { name: 'Comprovante NF-002.pdf', kind: 'PDF', size: '160 KB', date: '30/01/2026' },
  ];
  return <MockPageShell title="Meus Documentos" breadcrumbs={bcC('Documentos')}
    table={<MockTable columns={[{ key: 'name', label: 'Arquivo' }, { key: 'kind', label: 'Tipo' }, { key: 'size', label: 'Tamanho', align: 'right' }, { key: 'date', label: 'Data' }]} rows={rows} />}
  />;
}

export function PortalOS() {
  return <MockPageShell title="Minhas Ordens de Serviço" breadcrumbs={bcC('Ordens de Serviço')}
    table={<MockTable columns={[{ key: 'number', label: 'Nº', mono: true }, { key: 'vehicle', label: 'Veículo', mono: true }, { key: 'type', label: 'Tipo' }, { key: 'status', label: 'Status', status: true }, { key: 'opened', label: 'Abertura' }]} rows={mockOS} statusMap={{ Aberta: 'blue', 'Em Andamento': 'amber', Finalizada: 'green', 'Aguardando Peças': 'amber' }} />}
  />;
}

export function PortalSolicitacoes() {
  const rows = [
    { id: 'SOL-021', title: 'Solicitação de veículo extra 15/03', kind: 'Extra', status: 'Aprovada', date: '08/02/2026' },
    { id: 'SOL-020', title: 'Alteração de horário rota R-002', kind: 'Alteração', status: 'Em análise', date: '05/02/2026' },
  ];
  return <MockPageShell title="Solicitações" subtitle="Solicite alterações, viagens extras e suporte"
    breadcrumbs={bcC('Solicitações')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova solicitação</Button>}
    table={<MockTable columns={[{ key: 'id', label: 'Nº', mono: true }, { key: 'title', label: 'Título' }, { key: 'kind', label: 'Tipo' }, { key: 'date', label: 'Data' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Aprovada: 'green', 'Em análise': 'amber' }} />}
  />;
}

export function PortalMensagens() {
  const msgs = [
    { from: 'Fernanda Lima (Financeiro)', subject: 'NF-002 - Aguardando pagamento', preview: 'Sua fatura está próxima do vencimento em 5 dias...', time: '2h atrás', unread: true },
    { from: 'Marcelo Rodrigues (Manutenção)', subject: 'Vistoria programada para 15/02', preview: 'Confirmamos vistoria dos veículos do contrato para...', time: '1d atrás', unread: true },
    { from: 'Ricardo Bastos (Comercial)', subject: 'Renovação de contrato', preview: 'Iniciamos as tratativas de renovação para o próximo ano...', time: '3d atrás', unread: false },
  ];
  return <MockPageShell title="Central de Mensagens" breadcrumbs={bcC('Mensagens')}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 divide-y divide-neutral-100 dark:divide-neutral-800">{msgs.map((m, i) => (
      <div key={i} className={`px-5 py-4 flex items-start gap-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/40 cursor-pointer ${m.unread ? 'bg-brand/5' : ''}`}>
        {m.unread && <span className="h-2 w-2 rounded-full bg-brand mt-2 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between mb-0.5"><p className="font-medium text-sm truncate">{m.from}</p><p className="text-[10px] text-neutral-500 whitespace-nowrap">{m.time}</p></div>
          <p className="font-semibold text-sm">{m.subject}</p>
          <p className="text-xs text-neutral-500 mt-0.5 truncate">{m.preview}</p>
        </div>
      </div>
    ))}</div>}
  />;
}

export function PortalPerfil() {
  return <MockPageShell title="Meu Perfil" breadcrumbs={bcC('Perfil')}
    extra={<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 text-center">
        <div className="h-20 w-20 rounded-full bg-brand text-white flex items-center justify-center text-2xl font-bold mx-auto">PC</div>
        <h3 className="font-semibold mt-3">Prefeitura de Canoas</h3>
        <p className="text-xs text-neutral-500">CNPJ 87.123.456/0001-22</p>
        <p className="text-xs text-neutral-500 mt-1">Cliente desde 2020</p>
      </div>
      <div className="md:col-span-2 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
        <h3 className="font-semibold mb-3">Informações</h3>
        <dl className="text-sm grid grid-cols-2 gap-y-2">
          <dt className="text-neutral-500">Contato principal</dt><dd>Sec. Transporte — canoas.rs.gov.br</dd>
          <dt className="text-neutral-500">E-mail</dt><dd>sec.transporte@canoas.rs.gov.br</dd>
          <dt className="text-neutral-500">Telefone</dt><dd>(51) 3462-0000</dd>
          <dt className="text-neutral-500">Endereço</dt><dd>Rua XV de Novembro, 43 - Canoas/RS</dd>
        </dl>
        <Button size="sm" variant="outline" className="mt-4">Editar dados</Button>
      </div>
    </div>}
  />;
}

// ============ Portal do Motorista ============

const bcM = (l) => [{ label: 'Portal do Motorista', to: '/portal-motorista' }, { label: l }];

export function PortalMotoristaDashboard() {
  return <MockPageShell title="Bom dia, Carlos Silva 🚌" subtitle="Sua próxima viagem sai em 45 minutos"
    breadcrumbs={[{ label: 'Portal do Motorista' }]}
    kpis={[
      <MockKpi key="1" icon={CalendarDays} label="Viagens hoje" value="4" />,
      <MockKpi key="2" icon={ClipboardCheck} label="Checklists hoje" value="1" tone="warn" />,
      <MockKpi key="3" icon={Fuel} label="Última km/L" value="3.2" tone="info" />,
      <MockKpi key="4" icon={IdCard} label="CNH válida até" value="06/2027" tone="success" />,
    ]}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <h3 className="font-semibold mb-3">Próximas viagens</h3>
      <ol className="space-y-3">{mockTrips.slice(0, 4).map(t => (
        <li key={t.id} className="flex items-center gap-3 p-3 rounded-lg border border-neutral-100 dark:border-neutral-800">
          <div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><Truck className="h-5 w-5" /></div>
          <div className="flex-1"><p className="font-medium text-sm">{t.route} — {t.code}</p><p className="text-xs text-neutral-500">{t.departure} → {t.arrival} • Veículo {t.vehicle}</p></div>
          <StatusBadge value={t.status} tone={t.status === 'Concluída' ? 'green' : t.status === 'Em andamento' ? 'blue' : 'amber'} />
        </li>
      ))}</ol>
    </div>}
  />;
}

export function MotoristaEscalas() {
  const days = ['Seg 10/02', 'Ter 11/02', 'Qua 12/02', 'Qui 13/02', 'Sex 14/02', 'Sáb 15/02', 'Dom 16/02'];
  return <MockPageShell title="Minhas Escalas" breadcrumbs={bcM('Escalas')}
    extra={<div className="grid grid-cols-1 md:grid-cols-7 gap-2">{days.map((d, i) => (
      <div key={d} className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-3">
        <p className="text-xs font-semibold text-neutral-500">{d}</p>
        {i === 3 ? <p className="text-xs mt-2 bg-neutral-200 dark:bg-neutral-800 rounded px-2 py-1 inline-block">FOLGA</p> :
          <><p className="text-sm font-medium mt-2">06:00 - 14:00</p><p className="text-xs text-neutral-500">R-001 Interbairros</p></>}
      </div>
    ))}</div>}
  />;
}

export function MotoristaViagens() {
  return <MockPageShell title="Minhas Viagens" breadcrumbs={bcM('Viagens')}
    table={<MockTable columns={[{ key: 'code', label: 'Nº', mono: true }, { key: 'route', label: 'Rota', mono: true }, { key: 'vehicle', label: 'Veículo', mono: true }, { key: 'date', label: 'Data' }, { key: 'departure', label: 'Saída' }, { key: 'arrival', label: 'Chegada' }, { key: 'status', label: 'Status', status: true }]} rows={mockTrips} statusMap={{ Concluída: 'green', 'Em andamento': 'blue', Agendada: 'amber' }} />}
  />;
}

export function MotoristaChecklists() {
  const rows = [
    { id: 'CK-124', date: '10/02/2026 05:45', vehicle: 'ABC-1234', template: 'Pré-viagem', criticos: 0, status: 'OK' },
    { id: 'CK-123', date: '09/02/2026 18:30', vehicle: 'ABC-1234', template: 'Pós-viagem', criticos: 1, status: 'Atenção' },
    { id: 'CK-122', date: '09/02/2026 05:50', vehicle: 'ABC-1234', template: 'Pré-viagem', criticos: 0, status: 'OK' },
  ];
  return <MockPageShell title="Meus Checklists" breadcrumbs={bcM('Checklists')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Iniciar checklist</Button>}
    table={<MockTable columns={[{ key: 'id', label: 'Nº', mono: true }, { key: 'date', label: 'Data' }, { key: 'vehicle', label: 'Veículo', mono: true }, { key: 'template', label: 'Tipo' }, { key: 'criticos', label: 'Críticos', align: 'right' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ OK: 'green', Atenção: 'amber' }} />}
  />;
}

export function MotoristaAbastecimentos() {
  return <MockPageShell title="Meus Abastecimentos" breadcrumbs={bcM('Abastecimentos')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Registrar</Button>}
    table={<MockTable columns={[{ key: 'date', label: 'Data' }, { key: 'vehicle', label: 'Veículo', mono: true }, { key: 'station', label: 'Posto' }, { key: 'liters', label: 'Litros', align: 'right' }, { key: 'total', label: 'Total', align: 'right' }, { key: 'kmL', label: 'km/L', align: 'right' }]} rows={mockFuel.map(f => ({...f, total: `R$ ${f.total.toFixed(2)}`}))} />}
  />;
}

export function MotoristaOcorrencias() {
  const rows = [
    { id: 'OC-018', kind: 'Acidente leve', description: 'Colisão leve na traseira - sem vítimas', date: '05/02/2026', status: 'Encerrada' },
    { id: 'OC-017', kind: 'Reclamação passageiro', description: 'Aviso de ruído no motor', date: '02/02/2026', status: 'Em análise' },
    { id: 'OC-016', kind: 'Objeto esquecido', description: 'Mochila azul - devolvida na garagem', date: '28/01/2026', status: 'Encerrada' },
  ];
  return <MockPageShell title="Ocorrências" breadcrumbs={bcM('Ocorrências')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Reportar ocorrência</Button>}
    table={<MockTable columns={[{ key: 'id', label: 'Nº', mono: true }, { key: 'kind', label: 'Tipo' }, { key: 'description', label: 'Descrição' }, { key: 'date', label: 'Data' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Encerrada: 'green', 'Em análise': 'amber' }} />}
  />;
}

export function MotoristaDocumentos() {
  const rows = [
    { doc: 'CNH categoria D', number: '01234567891', expiry: '02/06/2027', status: 'OK' },
    { doc: 'Exame periódico', number: '-', expiry: '15/03/2026', status: 'Próx. vencimento' },
    { doc: 'Curso Direção Defensiva', number: 'DD-2024-142', expiry: '10/06/2026', status: 'OK' },
    { doc: 'MOPP', number: 'MOPP-2023-089', expiry: '20/10/2025', status: 'Vencido' },
  ];
  return <MockPageShell title="Meus Documentos" breadcrumbs={bcM('Documentos')}
    table={<MockTable columns={[{ key: 'doc', label: 'Documento' }, { key: 'number', label: 'Número', mono: true }, { key: 'expiry', label: 'Vencimento' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ OK: 'green', 'Próx. vencimento': 'amber', Vencido: 'red' }} />}
  />;
}

export function MotoristaAgenda() {
  return <MockPageShell title="Minha Agenda" breadcrumbs={bcM('Agenda')}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <h3 className="font-semibold mb-3">Fevereiro / 2026</h3>
      <div className="grid grid-cols-7 gap-1">
        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => <div key={i} className="text-center text-[11px] text-neutral-500 py-2">{d}</div>)}
        {Array.from({ length: 35 }).map((_, i) => {
          const day = i - 3;
          const active = day > 0 && day <= 28;
          const off = active && (day % 7 === 3);
          return <div key={i} className={`aspect-square rounded p-1 text-xs ${active ? 'border border-neutral-200 dark:border-neutral-800' : ''} ${day === 10 ? 'ring-2 ring-brand' : ''}`}>
            {active && day}
            {off && <div className="mt-0.5 text-[8px] text-neutral-500 uppercase">Folga</div>}
            {active && !off && <div className="mt-0.5 text-[8px] bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded px-1">06-14h</div>}
          </div>;
        })}
      </div>
    </div>}
  />;
}

export function MotoristaPerfil() {
  return <MockPageShell title="Meu Perfil" breadcrumbs={bcM('Perfil')}
    extra={<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 text-center">
        <div className="h-20 w-20 rounded-full bg-brand text-white flex items-center justify-center text-2xl font-bold mx-auto">CS</div>
        <h3 className="font-semibold mt-3">Carlos Silva</h3>
        <p className="text-xs text-neutral-500">Motorista • Categoria D</p>
        <p className="text-xs text-neutral-500 mt-1">Desde 03/2019</p>
      </div>
      <div className="md:col-span-2 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
        <h3 className="font-semibold mb-3">Estatísticas do motorista</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { label: 'Km rodados 2026', value: '2.145' }, { label: 'Viagens no mês', value: '84' }, { label: 'Consumo médio', value: '3.4 km/L' },
            { label: 'Rating', value: '4.9 ⭐' }, { label: 'Ocorrências', value: '2' }, { label: 'Prêmio anterior', value: 'Motorista destaque' },
          ].map((s, i) => (
            <div key={i} className="rounded-lg bg-neutral-50 dark:bg-neutral-900/50 p-3">
              <p className="text-[10px] text-neutral-500 uppercase">{s.label}</p><p className="font-semibold mt-1">{s.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>}
  />;
}
